import { useMemo, useState } from "react";
import { characters, type Character } from "@/mocks/characters";

const filters = ["全部", "主角", "女主", "导师", "吉祥物", "同事"];

function CharacterGrid() {
  const [active, setActive] = useState("全部");
  const [selected, setSelected] = useState<Character | null>(null);

  const list = useMemo(() => {
    if (active === "全部") return characters;
    return characters.filter((c) => c.role.split("·")[0] === active);
  }, [active]);

  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setActive(f)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              active === f
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-800"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setSelected(c)}
            className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 text-left transition-transform hover:-translate-y-1"
          >
            <div className="aspect-[3/3.4] overflow-hidden">
              <img
                src={c.image}
                alt={`角色设定：${c.name}`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{c.name}</h3>
                <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800">
                  {c.role.split("·")[0]}
                </span>
              </div>
              <p className="mt-1 text-xs text-foreground-500">{c.role} · {c.age}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.tags.slice(0, 2).map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${selected.name} 角色档案`}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr]">
              <div className="aspect-[3/4] sm:aspect-auto">
                <img
                  src={selected.image}
                  alt={`${selected.name} 档案配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                    {selected.role} · {selected.age}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    aria-label="关闭"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-primary-100"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                <h3 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
                  {selected.name}
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                      {t}
                    </span>
                  ))}
                </div>
                <blockquote className="mt-5 border-l-4 border-primary-500 pl-4 font-heading text-base italic text-primary-800">
                  「{selected.motto}」
                </blockquote>
                <p className="mt-5 text-sm leading-7 text-foreground-700">{selected.intro}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CharacterGrid;